import React, { useMemo, useState } from 'react';
import { TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { PlayerProgressItem } from '@/features/player/components/types';
import { formatProgressBadgeTitleCompact } from '@/features/player/utils/progress-title';
import { useScaling } from '@/hooks';
import { colors, withOpacity } from '@/theme/colors';

import { PlayerProfileLevelDetailsSheet } from './player-profile-level-details-sheet';
import {
  formatDateEarnedShort,
  formatDisplayDate,
  parseLevelNumber,
} from './progress-tracker.utils';
import { useStyles } from './styles';

export function CompletedLevelCard({ item }: { item: PlayerProgressItem }) {
  const styles = useStyles();
  const { isTablet, scale } = useScaling();
  const copy = STRINGS.player.profile.progressTrackerContent;
  const [detailsOpen, setDetailsOpen] = useState(false);

  const levelNumber = useMemo(() => parseLevelNumber(item.name) ?? 1, [item.name]);
  const levelColor = useMemo(
    () => colors[`Level ${levelNumber}` as keyof typeof colors] ?? colors['Level 9'],
    [levelNumber],
  );
  const levelColor20 = useMemo(() => withOpacity(levelColor, 0.2), [levelColor]);
  const mastered = useMemo(() => formatDisplayDate(item.dateCompleted), [item.dateCompleted]);
  const masteredText =
    mastered && mastered !== '-'
      ? `${copy.masteredLabel} ${copy.onLabel} ${mastered}`
      : copy.masteredLabel;

  return (
    <>
      <Stack style={styles.card}>
        <Stack direction="row">
          <Stack
            style={[
              styles.cardAccent,
              { backgroundColor: levelColor, borderTopLeftRadius: scale(2) },
            ]}
          />
          <Stack
            flex={1}
            paddingX={scale(9)}
            paddingY={scale(12)}
            gap={scale(4)}
            style={styles.cardHeaderRow}>
            <Text
              fontWeight={'800'}
              fontSize={scale(10)}
              fontFamily="Montserrat"
              style={[styles.levelTitle, { color: levelColor }]}>
              {`LEVEL ${levelNumber}`}
            </Text>
            <Text fontWeight={'400'} fontSize={scale(8)} fontFamily="Inter" style={styles.subText}>
              {masteredText}
            </Text>
          </Stack>
        </Stack>

        <Stack style={styles.divider} />

        <Stack direction="row">
          <Stack
            style={[
              styles.cardAccent,
              { backgroundColor: levelColor20, borderBottomLeftRadius: scale(2) },
            ]}
          />
          <Stack
            paddingX={scale(12)}
            paddingTop={scale(12)}
            paddingBottom={scale(10)}
            style={styles.cardBodyRow}
            flex={1}>
            <Stack
              direction="row"
              gap={scale({ phone: 0, tablet: 12 })}
              justify="center"
              style={styles.wrapRow}>
              {(item.badges ?? []).map((b, idx) => (
                <Stack key={`${b.name}-${idx}`} align="center" width={scale(48)} gap={scale(2)}>
                  {b.badgeEarned ? (
                    <SvgIcon name="IcCompleted" size={scale(24)} />
                  ) : (
                    <SvgIcon name="IcClock" size={scale(24)} color={colors['Blue 2']} />
                  )}
                  <Text
                    fontWeight={'400'}
                    fontSize={scale(7)}
                    fontFamily="Inter"
                    style={[
                      styles.completedBadgeLabel,
                      { color: b.badgeEarned ? colors.Success : colors['Blue 2'] },
                    ]}
                    numberOfLines={2}>
                    {formatProgressBadgeTitleCompact(b.name)}
                  </Text>
                  <Text
                    fontWeight={'400'}
                    fontSize={scale(7)}
                    fontFamily="Inter"
                    style={[
                      styles.completedBadgeSub,
                      { color: b.badgeEarned ? colors.Success : colors['Blue 2'] },
                    ]}>
                    {b.badgeEarned ? copy.completed : copy.incomplete}
                  </Text>
                  <Text
                    fontWeight={'400'}
                    fontSize={scale(7)}
                    fontFamily="Inter"
                    style={styles.completedBadgeSub}>
                    {b.badgeEarned ? formatDateEarnedShort(b.dateEarned) : copy.na}
                  </Text>
                </Stack>
              ))}
            </Stack>

            <TouchableOpacity
              style={[styles.detailsButton, { height: scale(24) }]}
              onPress={() => setDetailsOpen(true)}>
              <Text
                variant={isTablet ? undefined : 'Font Montserrat/Subtitle/Minimize 2 Medium 10px'}
                style={[
                  styles.detailsText,
                  { color: colors.Blue },
                  isTablet ? styles.detailsTextTablet : undefined,
                ]}>
                {copy.details}
              </Text>
              <SvgIcon
                color={'#E97D27'}
                name="IcBack"
                size={scale(18)}
                style={styles.detailsIcon}
              />
            </TouchableOpacity>
          </Stack>
        </Stack>
      </Stack>

      <PlayerProfileLevelDetailsSheet
        open={detailsOpen}
        item={detailsOpen ? item : null}
        onOpenChange={setDetailsOpen}
      />
    </>
  );
}
